/**
 * This service is in charge of handle ISC and STORAGE logic
 * 1.- Add a new charge
 *  a.- If charge is storage then validate if ISC is paid or ISC is added to the cart
 * 2.- Remove a charge
 *  a.- If charge is ISC then validate if STORAGE is added to the cart, if is added then remove it.
 */
import { Injectable } from '@angular/core';
import {
    CartException,
    CartExceptionType,
    PaymentCart,
    PaymentCartRequest,
    PaymentType,
} from '@cargos/sprintpay-models';
import { Observable, map, of, switchMap, throwError } from 'rxjs';
import { AwbLookUpService } from './awb-look-up.service';
import { CartBillService } from './cart.service';
import { ConfigService } from './config.service';

@Injectable({
    providedIn: 'root',
})
export class ISCAndStorageCartService {
    constructor(
        private awbLookUpService: AwbLookUpService,
        private cartBillService: CartBillService,
        private configService: ConfigService
    ) {}

    isStorage$(paymentCart: PaymentCart): Observable<boolean> {
        return of((paymentCart.externalData?.paymentType ?? '').toLowerCase() === PaymentType.STORAGE);
    }

    isISC(paymentType: string): boolean {
        return paymentType.toLocaleLowerCase() === PaymentType.ISC;
    }

    isHawbISCPaid(paymentCart: PaymentCartRequest): Observable<boolean> {
        return this.awbLookUpService
            .getHawbsCharges$()
            .pipe(
                map((hawbs) =>
                    hawbs.some(
                        (item) => this.isISC(item.paymentType) && item.isPaid && item.hawbId === paymentCart.hawb
                    )
                )
            );
    }

    isAWBISCPaid(): Observable<boolean> {
        return this.awbLookUpService
            .getAwbsCharges$()
            .pipe(map((awbs) => awbs.some((charge) => this.isISC(charge.paymentType) && charge.isPaid)));
    }

    isISCPaid(paymentCart: PaymentCartRequest): Observable<boolean> {
        if (paymentCart.hawb || paymentCart.externalData?.hawbId) {
            return this.isHawbISCPaid(paymentCart);
        }

        return this.isAWBISCPaid();
    }

    isISCAddedInCart(paymentCart: PaymentCartRequest): Observable<boolean> {
        return this.cartBillService.getCart$().pipe(
            map((cart) => {
                if (cart[0]?.hawb) {
                    return cart.some((item) => {
                        return (
                            item.externalData?.paymentType?.toLowerCase() === PaymentType.ISC &&
                            item.hawb === paymentCart.hawb
                        );
                    });
                }

                return cart.some((item) => item.externalData?.paymentType?.toLowerCase() === PaymentType.ISC);
            })
        );
    }

    validateISCBeforeAddToCart(paymentCart: PaymentCartRequest): Observable<boolean> {
        return of(this.isISC(paymentCart.externalData?.paymentType || '')).pipe(
            switchMap((isISC) => {
                if (isISC) {
                    return of(true);
                }

                return this.isPaymentTypeAllowedToPayWithoutISC(paymentCart).pipe(
                    switchMap((isPaymentAlowed) => {
                        if (isPaymentAlowed) {
                            return of(true);
                        }

                        return this.isISCPaid(paymentCart).pipe(
                            switchMap((isISCPaid) => {
                                if (isISCPaid) {
                                    return of(true);
                                }

                                return this.isISCAddedInCart(paymentCart).pipe(
                                    switchMap((isISCAddedInCart) => {
                                        if (isISCAddedInCart) {
                                            return of(true);
                                        }

                                        return throwError(() =>
                                            CartException.fromJson({
                                                code: CartExceptionType.ISC_REQUIRED,
                                            })
                                        );
                                    })
                                );
                            })
                        );
                    })
                );
            })
        );
    }

    isPaymentTypeAllowedToPayWithoutISC(paymentCart: PaymentCartRequest): Observable<boolean> {
        return of(
            this.configService.getPaymentTypesListNoNeedISC().includes(paymentCart.externalData?.paymentType || '')
        );
    }

    validateISCBeforeRemoveToCart(paymentCart: PaymentCart): Observable<boolean> {
        return of(this.isISC(paymentCart.externalData?.paymentType || '')).pipe(map((isIsc) => !isIsc));
    }
}
